import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { TextField } from "@/components/Interface/TextField/TextFields";
import { useMagicLinkLogin } from "@/hooks/useMagicLinkLogin";
import { FCC } from "@/types";

type MagicLinkEmailFields = {
  emailAddress: string;
};

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

export const MagicLinkRequestForm: FCC<Props> = ({ onBack, onSuccess }) => {
  const { t } = useTranslation();
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<MagicLinkEmailFields>({
    defaultValues: { emailAddress: "" },
  });
  const { requestMagicLink, showRequestError, isRequesting } =
    useMagicLinkLogin();
  const [forceEnableButton, setForceEnableButton] = useState(false);

  useEffect(() => {
    if (isRequesting || isSubmitting) {
      setForceEnableButton(false);
      const timer = setTimeout(() => setForceEnableButton(true), 10000);

      return () => clearTimeout(timer);
    }

    setForceEnableButton(false);
  }, [isRequesting, isSubmitting]);

  const onSubmit = async (data: MagicLinkEmailFields) => {
    const result = await requestMagicLink(data.emailAddress);

    if (result.ok) {
      onSuccess();
    } else {
      showRequestError(result.reason);
    }
  };

  return (
    <form
      className="max-w-[420px] md:m-auto md:h-max md:w-full"
      onSubmit={handleSubmit(onSubmit)}
      data-testid="magic-link-request-form"
    >
      <h1 className="mb-4 text-4xl font-semibold text-white">
        {t("translation:newWelcome.magicLink.request.title")}
      </h1>
      <p className="mb-10 text-sm text-white">
        {t("translation:newWelcome.magicLink.request.description")}
      </p>

      <TextField
        id="magicLinkEmailAddress"
        error={errors.emailAddress}
        inputType="email"
        labelText={t("translation:Common.generalForm.fields.email.label")}
        placeholder={t(
          "translation:Common.generalForm.fields.email.placeholder",
        )}
        register={register("emailAddress", {
          required: t(
            "translation:Common.generalForm.validation.emailRequired",
          ),
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: t(
              "translation:Common.generalForm.validation.emailNotValid",
            ),
          },
        })}
      />

      <Button
        fullWidth
        className="accent mb-4 mt-10"
        type="submit"
        disabled={!forceEnableButton && (isRequesting || isSubmitting)}
        dataTestid="magic-link-request-submit"
      >
        {t("translation:newWelcome.magicLink.request.submitButton")}
      </Button>

      <button
        type="button"
        onClick={onBack}
        className="inline cursor-pointer text-xs font-semibold text-highlight-100 focus-within:underline hover:underline focus:underline focus-visible:underline active:underline"
      >
        {t("translation:newWelcome.magicLink.request.backToPasswordLogin")}
      </button>
    </form>
  );
};
